/**
 * useAppearanceEffect — applies dark/light mode from Dexie settings.
 *
 * Reads `#appearance` ("system" | "light" | "dark") from Dexie and toggles
 * the `.dark` class on `<html>`. Also mirrors the value to localStorage
 * so the blocking inline script in index.html can apply the correct theme
 * before React mounts (prevents flash-of-light-mode).
 *
 * @see index.html inline script for synchronous first-paint handling
 * @see SettingsAppearance.tsx for the user-facing theme picker
 */

import { useEffect } from "react"
import { useSetting } from "@/features/settings/hooks/useSetting"

const LS_KEY = "totodo:appearance"

function resolveIsDark(appearance: string): boolean {
  if (appearance === "dark") return true
  if (appearance === "light") return false
  // "system" or unknown — follow OS preference
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

export function useAppearanceEffect(): void {
  const { value: appearance } = useSetting("#appearance", "system")

  // Keep localStorage in sync so the inline script picks it up on reload
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, appearance)
    } catch {
      // localStorage unavailable
    }
  }, [appearance])

  // Apply .dark class + listen for OS preference changes in "system" mode
  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle("dark", resolveIsDark(appearance))

    if (appearance === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)")
      const handler = (e: MediaQueryListEvent) => {
        root.classList.toggle("dark", e.matches)
      }
      mq.addEventListener("change", handler)
      return () => mq.removeEventListener("change", handler)
    }
  }, [appearance])
}
